// src/pages/MyChefBookings.jsx
/* eslint-disable react/prop-types */
/**
 * MyChefBookings.jsx
 *
 * Production-ready page for managing chef bookings.
 * - Realtime listener to Firestore via listenToBookings (keeps your helper)
 * - Admin-only export & copy JSON actions
 * - Pagination, filtering, search (debounced)
 * - Status updates for admins (through updateBookingStatus helper)
 * - Accessibility improvements and ESLint-friendly roles (no redundant roles)
 *
 * Notes:
 *  - Retains the same visual tone (cards, soft shadows, light palette).
 *  - Kept existing classNames so your CSS file continues to work.
 *  - Ensures no redundant 'role="article"' on <article> elements to avoid build
 *
 * Author: assistant (updated for Vercel build)
 * Date: 2025-11-09
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../context/AuthContext";
import {
  listenToBookings,
  updateBookingStatus as updateBookingStatusHelper,
} from "../utils/firebase";
import PropTypes from "prop-types";
import "../styles/MyChefBookings.css";
import BookingPageShell from "../components/BookingPageShell"; // 🔽 ADDED IMPORT

/* ========================================================================
   CONFIGURATION / CONSTANTS
   ======================================================================== */

/**
 * Admin emails - keep in sync with your backend
 * Lowercase expected.
 */
const ADMIN_EMAILS = [
  "hariharanmadhav@gmail.com",
  "mugiwaraaluffy007@gmail.com",
  "lalithaganesan449@gmail.com",
];

/**
 * Booking status options - order matters where we sort by status index
 */
const STATUS_LIST = [
  "pending",
  "accepted",
  "confirmed",
  "completed",
  "cancelled",
];

const SEARCH_DEBOUNCE_MS = 220;
const ITEMS_PER_PAGE = 12;

/**
 * CSV fields exported in CSV files - keeps parity with previous file
 */
const CSV_EXPORT_FIELDS = [
  "id",
  "chefName",
  "chef",
  "eventName",
  "menu",
  "date",
  "time",
  "guests",
  "persons",
  "location",
  "venue",
  "userEmail",
  "email",
  "notes",
  "status",
  "createdAt",
  "adminNote",
];

/* ========================================================================
   SMALL UTILITIES
   - Keep these pure and well-tested. They are intentionally verbose and
     defensive so we can reason about transformations easily.
   ======================================================================== */

/**
 * Normalize a value to lower-case string for comparisons/searches
 * @param {*} v any value
 * @returns {string}
 */
const normalize = (v) => (v || "").toString().toLowerCase().trim();

/**
 * Format a Firestore Timestamp, Date, or string into a locale string
 * Safe and defensive.
 * @param {*} val
 * @returns {string}
 */
const formatDate = (val) => {
  if (!val) return "Unknown";
  if (typeof val?.toDate === "function") {
    try {
      return val.toDate().toLocaleString();
    } catch (e) {
      return String(val);
    }
  }
  if (val instanceof Date) return val.toLocaleString();
  const parsed = new Date(val);
  if (!isNaN(parsed.getTime())) return parsed.toLocaleString();
  return String(val);
};

/**
 * Convert a date-like value to ISO string for CSV export when possible.
 * @param {*} val
 * @returns {string}
 */
const formatDateForCSV = (val) => {
  if (!val) return "";
  try {
    let date;
    if (typeof val?.toDate === "function") {
      date = val.toDate();
    } else if (val instanceof Date) {
      date = val;
    } else {
      date = new Date(val);
    }
    if (isNaN(date.getTime())) return String(val);
    return date.toISOString();
  } catch (e) {
    return String(val);
  }
};

/**
 * Convert array of objects to CSV string using provided fields
 * Helps export feature.
 * @param {Array} arr
 * @param {Array} fields
 * @returns {string}
 */
const toCSV = (arr = [], fields = []) => {
  const esc = (v = "") =>
    `"${String(v).replace(/"/g, '""').replace(/\n/g, " ").replace(/\r/g, " ")}"`;
  const header = fields.map((f) => esc(f)).join(",");

  const rows = arr.map((item) =>
    fields
      .map((field) => {
        if (field === "createdAt") return esc(formatDateForCSV(item[field]));
        return esc(item[field] ?? "");
      })
      .join(",")
  );

  return [header, ...rows].join("\r\n");
};

/**
 * Download a blob safely, with object URL cleanup
 * @param {string} filename
 * @param {string} content
 * @param {string} mime
 */
const downloadBlob = (filename, content, mime = "text/csv;charset=utf-8;") => {
  try {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  } catch (e) {
    console.error("downloadBlob error:", e);
    alert("Download failed. See console for details.");
  }
};

/**
 * Safe clipboard write (writes text to clipboard and falls back to textarea)
 * Returns boolean success
 * @param {string} text
 * @returns {Promise<boolean>}
 */
const safeClipboardWrite = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (e) {
    console.error("safeClipboardWrite failed:", e);
    return false;
  }
};

/* ========================================================================
   ICONS (small inline svgs) - kept minimal and accessible
   ======================================================================== */

const IconRefresh = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <path d="M21 12a9 9 0 1 0-3 6.36" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 3v6h-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
IconRefresh.propTypes = { size: PropTypes.number };

const IconDownload = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 15V3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
IconDownload.propTypes = { size: PropTypes.number };

const IconChevronDown = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
IconChevronDown.propTypes = { size: PropTypes.number };

/* ========================================================================
   SMALL PRESENTATIONAL COMPONENTS
   - StatusPill, SmallSpinner, HeaderButton
   - Kept CSS classes to match your stylesheet
   ======================================================================== */

/**
 * Status pill: small visual indicator for booking status
 */
const StatusPill = ({ status }) => {
  const s = normalize(status || "pending");
  const cls = `status-pill status-${s}`;
  return <span className={cls} aria-label={`Status: ${s}`}>{s}</span>;
};
StatusPill.propTypes = { status: PropTypes.string };

/**
 * Small spinner for loading states
 */
const SmallSpinner = () => <span className="small-spinner" aria-hidden="true" />;

/**
 * HeaderButton: consistent header action button
 */
const HeaderButton = ({ onClick, disabled, children, ariaLabel }) => (
  <button className="btn small" onClick={onClick} disabled={disabled} aria-label={ariaLabel}>
    {children}
  </button>
);
HeaderButton.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  ariaLabel: PropTypes.string,
};

/* ========================================================================
   CHEF BOOKING CARD
   - Memoized, keeps layout identical to your previous file
   - IMPORTANT: removed redundant role="article" to satisfy ESLint
   ======================================================================== */

const ChefBookingCard = React.memo(function ChefBookingCard({
  booking,
  isAdmin,
  onClick,
  onStatusChange,
  statusUpdatingId,
  statusUpdating,
}) {
  const st = normalize(booking.status || "pending");
  const updating = statusUpdating && statusUpdatingId === booking.id;

  const stopPropagation = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
  };

  return (
    <article
      className="ch-card"
      tabIndex="0"
      aria-labelledby={`bk-${booking.id}-title`}
      onClick={() => onClick?.(booking)}
      onKeyDown={(e) => ((e.key === "Enter" || e.key === " ") && onClick?.(booking))}
    >
      <div className="ch-head">
        <div style={{ flex: 1 }}>
          <h3 id={`bk-${booking.id}-title`} className="ch-titleText">
            {booking.chefName || booking.chef || "Chef Booking"}
          </h3>
          <div className="ch-meta">{booking.eventName || booking.menu || ""}</div>

          <div style={{ color: "#64748b", marginTop: 8, fontSize: 13 }}>
            <span style={{ marginRight: 8 }}>
              <strong>{booking.guests ?? booking.persons ?? "—"}</strong> guests
            </span>
            <span>{booking.location || booking.venue || "Unknown venue"}</span>
          </div>
        </div>

        <div style={{ minWidth: 120, textAlign: "right" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "center" }}>
            <StatusPill status={st} />
          </div>

          <div style={{ marginTop: 10, color: "#475569", fontSize: 12 }}>
            {booking.userEmail || booking.email || ""}
          </div>

          {isAdmin && (
            <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
              <select
                className="status-select"
                value={st}
                onClick={stopPropagation}
                onChange={(e) => onStatusChange(booking.id, e.target.value, booking)}
                disabled={updating}
                aria-label={`Change status for booking ${booking.id}`}
              >
                {STATUS_LIST.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>

              <button
                className="inline-btn"
                onClick={(e) => {
                  stopPropagation(e);
                  if (st === "cancelled") return;
                  if (window.confirm(`Mark booking "${booking.eventName || booking.id}" as cancelled?`)) {
                    onStatusChange(booking.id, "cancelled", booking);
                  }
                }}
                title="Quick cancel"
                disabled={updating}
                aria-label={`Quick cancel booking ${booking.id}`}
              >
                ❌
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card-body" aria-hidden="true">
        <div className="card-row">
          <div><b>Date:</b> {booking.date || "TBD"}</div>
          <div><b>Time:</b> {booking.time || "TBD"}</div>
        </div>

        <div className="card-row">
          <div><b>Menu:</b> {booking.menu || booking.eventName || "—"}</div>
        </div>

        <div className="card-row">
          <div><b>Notes:</b> {booking.notes || "—"}</div>
        </div>
      </div>

      <div className="card-foot">
        <div>Booked on: {formatDate(booking.createdAt)}</div>
        <div style={{ color: "#94a3b8", fontSize: "0.75rem" }}>{booking.id}</div>
      </div>
    </article>
  );
});
ChefBookingCard.displayName = "ChefBookingCard";
ChefBookingCard.propTypes = {
  booking: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  onClick: PropTypes.func,
  onStatusChange: PropTypes.func,
  statusUpdatingId: PropTypes.string,
  statusUpdating: PropTypes.bool,
};

/* ========================================================================
   ExportDropdown
   - Small dropdown for export choices. Accessible-ish.
   ======================================================================== */

const ExportDropdown = ({ onExportAll, onExportFiltered, onExportPage, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionClick = (handler) => {
    try {
      handler();
    } catch (e) {
      console.error("Export option handler failed:", e);
    } finally {
      setIsOpen(false);
    }
  };

  return (
    <div className="export-dropdown" ref={dropdownRef}>
      <button
        className="btn small"
        onClick={() => setIsOpen((v) => !v)}
        disabled={isLoading}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <IconDownload size={14} /> Export <IconChevronDown size={14} />
      </button>

      {isOpen && (
        <div className="export-menu" role="menu">
          <button className="export-option" onClick={() => handleOptionClick(onExportAll)} role="menuitem">Export All Bookings</button>
          <button className="export-option" onClick={() => handleOptionClick(onExportFiltered)} role="menuitem">Export Filtered Results</button>
          <button className="export-option" onClick={() => handleOptionClick(onExportPage)} role="menuitem">Export Current Page</button>
        </div>
      )}
    </div>
  );
};
ExportDropdown.propTypes = {
  onExportAll: PropTypes.func,
  onExportFiltered: PropTypes.func,
  onExportPage: PropTypes.func,
  isLoading: PropTypes.bool,
};

/* ========================================================================
   Main Page Component - MyChefBookings
   ======================================================================== */

const MyChefBookings = () => {
  const { currentUser, loading: authLoading } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [detail, setDetail] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const isMountedRef = useRef(true);
  const modalRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Admin check - memoized
  const isAdmin = useMemo(() => {
    return currentUser && ADMIN_EMAILS.includes(normalize(currentUser.email));
  }, [currentUser]);

  // Keyboard refresh shortcut (press 'r' when not typing)
  useEffect(() => {
    const handleKey = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (e.key === "r" && !e.ctrlKey && !e.metaKey && !["input", "textarea", "select"].includes(tag)) {
        e.preventDefault();
        setRefreshKey((k) => k + 1);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  // Realtime listener to bookings (chef)
  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!currentUser) {
      // No user signed in -> empty list
      setBookings([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    const uid = isAdmin ? null : currentUser.uid;

    let unsubscribe = null;
    try {
      // Use your existing helper exactly as you requested
      unsubscribe = listenToBookings(
        "chef",
        uid,
        (docs) => {
          if (!isMountedRef.current) return;
          setBookings(docs || []);
          setCurrentPage(1);
          setLoading(false);
        },
        isAdmin
      );
    } catch (err) {
      console.error("listenToBookings failed:", err);
      setError("Failed to connect to bookings.");
      setLoading(false);
    }

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") unsubscribe();
    };
  }, [authLoading, currentUser, isAdmin, refreshKey]);

  /* ---------------------------
     Derived lists: filtered, sorted, paginated
     --------------------------- */

  const visibleBookings = useMemo(() => {
    const term = normalize(debouncedSearch);
    const filtered = (bookings || []).filter((b) => {
      if (filterStatus !== "all" && normalize(b.status || "pending") !== normalize(filterStatus)) return false;
      if (!term) return true;
      const haystack = [
        b.chefName,
        b.chef,
        b.location,
        b.venue,
        b.notes,
        b.userEmail,
        b.email,
        b.menu,
        b.eventName,
        b.id,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });

    // Sort by status index then createdAt desc
    filtered.sort((a, b) => {
      const as = STATUS_LIST.indexOf(normalize(a.status || "pending"));
      const bs = STATUS_LIST.indexOf(normalize(b.status || "pending"));
      if (as !== bs) return as - bs;
      const atime = (a.createdAt?.toDate?.() || new Date(a.createdAt || 0)).getTime();
      const btime = (b.createdAt?.toDate?.() || new Date(b.createdAt || 0)).getTime();
      return btime - atime;
    });

    return filtered;
  }, [bookings, debouncedSearch, filterStatus]);

  // Pagination slicing
  const paginatedBookings = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return visibleBookings.slice(start, start + ITEMS_PER_PAGE);
  }, [visibleBookings, currentPage]);

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = { all: (bookings || []).length };
    STATUS_LIST.forEach((s) => (counts[s] = 0));
    (bookings || []).forEach((b) => {
      const s = normalize(b.status || "pending");
      if (counts[s] !== undefined) counts[s]++;
    });
    return counts;
  }, [bookings]);

  /* ---------------------------
     Event handlers: refresh, copyJSON, export, status update
     --------------------------- */

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  /**
   * copyJSON - copies current page JSON to clipboard.
   * Admin-only: function defends itself
   */
  const copyJSON = useCallback(async (options = { scope: "page" }) => {
    if (!isAdmin) {
      alert("Copy JSON is restricted to administrators only.");
      return;
    }
    try {
      let payload = [];
      if (options.scope === "page") payload = paginatedBookings;
      else if (options.scope === "filtered") payload = visibleBookings;
      else if (options.scope === "all") payload = bookings;
      else payload = paginatedBookings;

      const text = JSON.stringify(payload, null, 2);
      const ok = await safeClipboardWrite(text);
      if (ok) alert("JSON copied to clipboard.");
      else alert("Failed to copy JSON. Try again or use browser copy.");
    } catch (e) {
      console.error("copyJSON failed:", e);
      alert("Copy failed.");
    }
  }, [paginatedBookings, visibleBookings, bookings, isAdmin]);

  const exportToCSV = useCallback((data, filename) => {
    try {
      const csvData = toCSV(data, CSV_EXPORT_FIELDS);
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      const finalFilename = `${filename}_${timestamp}.csv`;
      downloadBlob(finalFilename, csvData);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data. Please try again.");
    }
  }, []);

  const handleExportAll = useCallback(() => {
    setExportLoading(true);
    setTimeout(() => {
      exportToCSV(bookings, "all_bookings");
      setExportLoading(false);
    }, 300);
  }, [bookings, exportToCSV]);

  const handleExportFiltered = useCallback(() => {
    if (!visibleBookings.length) {
      alert("No filtered results to export.");
      return;
    }
    setExportLoading(true);
    setTimeout(() => {
      exportToCSV(visibleBookings, "filtered_bookings");
      setExportLoading(false);
    }, 300);
  }, [visibleBookings, exportToCSV]);

  const handleExportPage = useCallback(() => {
    if (!paginatedBookings.length) {
      alert("No bookings on this page to export.");
      return;
    }
    setExportLoading(true);
    setTimeout(() => {
      exportToCSV(paginatedBookings, "current_page_bookings");
      setExportLoading(false);
    }, 300);
  }, [paginatedBookings, exportToCSV]);

  /**
   * update booking status via helper (keeps your helper naming)
   * Shows alerts on failure and updates local UI status flags
   */
  const handleStatusChange = useCallback(async (id, newStatus, booking) => {
    try {
      setStatusUpdating(true);
      setStatusUpdatingId(id);
      await updateBookingStatusHelper("chef", id, newStatus, booking);
    } catch (err) {
      console.error("Status update failed:", err);
      alert("Failed to update status");
    } finally {
      setStatusUpdating(false);
      setStatusUpdatingId(null);
    }
  }, []);

  /* ---------------------------
     Pagination helpers & UX
     --------------------------- */

  const totalPages = Math.max(1, Math.ceil(visibleBookings.length / ITEMS_PER_PAGE));

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    // scroll to top of list for better UX
    const el = document.querySelector(".ch-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* ---------------------------
     Modal focus trap / keyboard
     --------------------------- */
  useEffect(() => {
    if (!detail) return;
    const el = modalRef.current;
    if (!el) return;
    const first = el.querySelector("button, a, input, select, textarea, [tabindex]");
    if (first) first.focus();
    const onKey = (e) => { if (e.key === "Escape") setDetail(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail]);

  /* ---------------------------
     RENDER
     --------------------------- */

  return (
    <BookingPageShell // ✅ WRAPPED
      title={isAdmin ? "All Chef Bookings" : "My Chef Bookings"}
      isAdmin={isAdmin}
    >
      <div className="my-chef-page">
        <header className="ch-header" aria-label="Chef bookings header">
          <div className="ch-left">
            <h1 className="ch-title">My Chef Bookings</h1>
            <HeaderButton onClick={handleRefresh} disabled={loading} ariaLabel="Refresh bookings">
              <IconRefresh size={14} /> Refresh
            </HeaderButton>
          </div>

          <div className="ch-controls">
            <div className="ch-control">
              <input
                type="text"
                placeholder="Search bookings..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search bookings"
              />
            </div>

            <div className="ch-control">
              <label htmlFor="status-filter" className="sr-only">Status filter</label>
              <select id="status-filter" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} aria-label="Filter by status">
                <option value="all">All ({bookings.length})</option>
                {STATUS_LIST.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)} ({statusCounts[s] ?? 0})</option>
                ))}
              </select>
            </div>

            {/* Export dropdown visible only to admins */}
            {isAdmin && (
              <ExportDropdown
                onExportAll={handleExportAll}
                onExportFiltered={handleExportFiltered}
                onExportPage={handleExportPage}
                isLoading={exportLoading}
              />
            )}

            {/* Copy JSON in header — visible only to admins */}
            {isAdmin ? (
              <button
                className="btn small"
                onClick={() => copyJSON({ scope: "page" })}
                disabled={!paginatedBookings.length}
                aria-label="Copy current page as JSON"
              >
                Copy JSON
              </button>
            ) : (
              <span aria-hidden style={{ width: 0, height: 0, display: "none" }} />
            )}
          </div>
        </header>

        {error && <div className="error-text" role="alert">{error}</div>}

        {loading && (
          <div className="loading-text" role="status" aria-live="polite">
            <SmallSpinner /> Loading bookings...
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Quick stats */}
            <section className="mc-stats" aria-label="Booking stats">
              {STATUS_LIST.map((status) => (
                <div key={status} className="stat-item" role="status" aria-live="polite">
                  {status.charAt(0).toUpperCase() + status.slice(1)}: <strong>{statusCounts[status] ?? 0}</strong>
                </div>
              ))}
            </section>

            {/* Bookings grid / list */}
            <section className="ch-grid" aria-label="Chef bookings list">
              {paginatedBookings.length ? (
                paginatedBookings.map((b) => (
                  <ChefBookingCard
                    key={b.id}
                    booking={b}
                    isAdmin={isAdmin}
                    onClick={setDetail}
                    onStatusChange={handleStatusChange}
                    statusUpdatingId={statusUpdatingId}
                    statusUpdating={statusUpdating}
                  />
                ))
              ) : (
                <div className="empty-list" role="status" aria-live="polite">No bookings found for the selected filters.</div>
              )}
            </section>

            {/* Pagination */}
            <div className="pagination-controls" aria-label="Pagination">
              <button className="btn small" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page">Previous</button>
              <span aria-live="polite">Page {currentPage} of {totalPages}</span>
              <button className="btn small" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page">Next</button>
            </div>
          </>
        )}

        {/* Details modal */}
        {detail && (
          <div className="modal-backdrop" ref={modalRef} tabIndex={-1} role="dialog" aria-modal="true" onClick={(e) => e.target === modalRef.current && setDetail(null)}>
            <div className="modal" role="document" aria-label="Booking details">
              <button className="modal-close" onClick={() => setDetail(null)} aria-label="Close details">✕</button>

              <div className="modal-content">
                <h2>{detail.eventName || "Booking Details"}</h2>

                <div className="modal-row">
                  <div><b>Date:</b> {detail.date || "TBD"}</div>
                  <div><b>Time:</b> {detail.time || "TBD"}</div>
                </div>

                <div className="modal-row">
                  <div><b>Location:</b> {detail.location || detail.venue || "Unknown"}</div>
                  <div><b>Guests:</b> {detail.guests ?? detail.persons ?? "—"}</div>
                </div>

                <div className="modal-row">
                  <div><b>Menu:</b> {detail.menu || "—"}</div>
                  <div><b>Status:</b> {detail.status || "pending"}</div>
                </div>

                <div className="modal-row">
                  <div><b>Notes:</b> {detail.notes || "—"}</div>
                </div>

                <div className="modal-row">
                  <div><b>User Email:</b> {detail.userEmail || detail.email || "—"}</div>
                  <div><b>Created At:</b> {formatDate(detail.createdAt)}</div>
                </div>

                <div className="modal-row id-row">
                  <div><b>ID:</b> {detail.id}</div>
                </div>
              </div>

              <footer className="modal-footer">
                <div className="modal-actions">
                  <button className="btn small" onClick={() => setDetail(null)} aria-label="Close details">Close</button>

                  {/* Copy JSON in modal - admin only */}
                  {isAdmin ? (
                    <button className="btn small" onClick={() => copyJSON({ scope: "page" })} disabled={!paginatedBookings.length} aria-label="Copy current page as JSON">Copy JSON (page)</button>
                  ) : <div style={{ minWidth: 8 }} aria-hidden />}

                  {/* Copy single booking JSON */}
                  {isAdmin && (
                    <button className="btn small" onClick={async () => {
                      if (!isAdmin) { alert("Copy JSON is restricted to administrators only."); return; }
                      try {
                        const ok = await safeClipboardWrite(JSON.stringify(detail, null, 2));
                        if (ok) alert("Booking JSON copied to clipboard.");
                        else alert("Copy failed.");
                      } catch (e) { console.error("copy single booking failed:", e); alert("Copy failed."); }
                    }} aria-label="Copy single booking JSON">
                      Copy Booking JSON
                    </button>
                  )}
                </div>
              </footer>
            </div>
          </div>
        )}
      </div>
    </BookingPageShell> // ✅ CLOSED
  );
};

export default MyChefBookings;

/* ================================================================
   End of MyChefBookings.jsx
   Notes:
    - Removed redundant role attribute from article elements to fix
      jsx-a11y/no-redundant-roles build error.
    - Keeps listenToBookings usage unchanged.
    - Keep CSS and classNames the same for visual parity.
   ================================================================ */